import { lazy, ReactElement, Suspense, SyntheticEvent, useContext, useState } from "react";
import {
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import {
  closeOutline,
  personOutline,
  logOutOutline,
  shareOutline,
  bugOutline,
  informationCircleOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import Icon from "../../atoms/Icon";
import AuthContext from "../../../contexts/AuthContext";
import { useStudySlug } from "../../../hooks/useStudySlug";
import MenuItem from "./MenuItem";
import LanguageSelectorModal from "../LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import ReactCountryFlag from "react-country-flag";
import { langToCountryCode } from "../../../helper/lang";
import { resetCaches } from "../../../services/questionnaire.service";
import { getProjectConfig } from "../../../projects";
import { settingsOutline } from "ionicons/icons";
import ReportBugModal from "../../../pages/Profile/ReportBugModal";
import AboutModal from "../../../pages/Profile/AboutModal";
import { useConfig } from "../../../contexts/ConfigContext";
import { useInvitationAffordances } from "../../../hooks/useInvitationAffordances";
import { InviteRelativeModal } from "../../organisms/Invitation/InviteModals";
import IconProfile from "../../../assets/MentalPlus/Profil.svg";
import IconSettings from "../../../assets/MentalPlus/Paramètres.svg";
import IconDeconnexion from "../../../assets/MentalPlus/Déconnexion.svg";
import IconEquipe from "../../../assets/MentalPlus/Equipe.svg";
import IconContact from "../../../assets/MentalPlus/Contact.svg";
import IconPartager from "../../../assets/MentalPlus/Partager.svg";

// Island settings belong to the lazy island-map experience — only island-map
// studies show the menu entry / load the chunk.
const IslandSettings = lazy(() =>
  import("../../../pages/MentalPlusPages").then((m) => ({
    default: m.IslandSettings,
  }))
);

interface MenuProps {
  isConnected: boolean;
  onClose(): void;
}

const Menu = ({ isConnected, onClose }: MenuProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const study_slug = useStudySlug();
  // The drawer docks on whichever side the project's header puts the burger
  // (Mental+ left, default right): the panel should emerge from under the
  // finger that summoned it, not from the opposite corner. Page overrides
  // never move the burger, so the PROJECT layout is the right source.
  const menuSide: "left" | "right" = getProjectConfig(study_slug).header?.left?.includes("menu")
    ? "left"
    : "right";
  const { user, endSession } = useContext(AuthContext);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportBugOpen, setReportBugOpen] = useState(false);
  const { study } = useConfig();
  const ionRouter = useIonRouter();

  const stopPropagation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  // "Profil" leads to the Compte page — the single account screen. It used to open
  // a second, parallel account UI (pages/Profile/Profile) that Compte had already
  // outgrown; two screens editing the same fields only drift apart.
  //
  // No tab-list guard: the /tabs/account ROUTE is registered for every study in
  // Tabs.tsx (a project's tab list only decides which BUTTONS the bar shows), so
  // the row always leads somewhere — a project can drop the Compte tab and still
  // be reached from here. (Mental+ went further and replaced the drawer outright
  // with the "account" header action, so it no longer renders this at all.)
  const openAccountTab = () => {
    onClose();
    ionRouter.push(`/${study_slug}/tabs/account`);
  };

  const logOut = async () => {
    await endSession();
    resetCaches();
    ionRouter.push(`/${study_slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

const [isAboutOpen, setIsAboutOpen] = useState(false);
const handleAbout = () => {
  setIsAboutOpen(true);
};

  // « Partager » / « Inviter … » — same rules, same code as the Compte page rows
  // (see useInvitationAffordances). Mental+ reaches them through Compte now; the
  // drawer keeps them for projects that still render it.
  const [inviteOpen, setInviteOpen] = useState(false);
  const { canShare, inviteRoleLabel, share: handleShare } =
    useInvitationAffordances(isConnected);

  return (
    <Modal $side={menuSide} onClick={onClose}>
      <MenuList $side={menuSide} onClick={stopPropagation}>
        <Header>
          <Icon icon={closeOutline} size="large" onClick={onClose} />
        </Header>

        <div>
          {isConnected && (
            <MenuItem
              icon={<MaskIcon $src={IconProfile} aria-hidden="true" />}
              title={t("mentalplus_menu_profile")}
              action={t("common_open")}
              onClick={openAccountTab}
            />
          )}

          {isConnected && getProjectConfig(study_slug).homeExperience === "island-map" && (
            <MenuItem
             icon={<MaskIcon $src={IconSettings} aria-hidden="true" />}
              title={t("mentalplus_menu_island_settings")}
              action={t("common_open")}
              onClick={() => setSettingsOpen(true)}
            />
          )}

          <MenuItem
            icon={
              <ReactCountryFlag
                countryCode={
                  langToCountryCode[language] || language.toUpperCase()
                }
                svg
                style={{
                  width: "1.5em",
                  height: "1.5em",
                  borderRadius: "3px",
                  objectFit: "cover",
                }}
                title={language}
              />
            }
            title={t(`lang_${language}`)}
            action={t("menu_language_button_text")}
            onClick={() => setModalVisible(true)}
          />

          {isConnected && inviteRoleLabel && (
            <MenuItem
              icon={<MaskIcon $src={IconPartager} aria-hidden="true" />}
              title={t("invite_relative_menu_title", { role: inviteRoleLabel })}
              action={t("invite_relative_menu_action")}
              onClick={() => setInviteOpen(true)}
            />
          )}

          {canShare && (
            <MenuItem
              icon={<MaskIcon $src={IconPartager} aria-hidden="true" />}
              title={t("mentalplus_menu_share")}
              action={t("menu_share")}
              onClick={handleShare}
            />
          )}

          {isConnected && (
            <MenuItem
              icon={<MaskIcon $src={IconContact} aria-hidden="true" />}
              title={t("mentalplus_menu_conatct")}
              action={t("common_open")}
              onClick={() => setReportBugOpen(true)}
            />
          )}

          <MenuItem
            icon={<MaskIcon $src={IconEquipe} aria-hidden="true" />}
            title={t("mentalplus_menu_equipe")}
            action={t("common_open")}
            onClick={handleAbout}
          />

          {isConnected && (
            <MenuItem
              icon={<MaskIcon $src={IconDeconnexion} aria-hidden="true" />}
              title={user?.attributes?.firstname || t("menu_logOut")}
              action={t("menu_logOut")}
              onClick={() => logOut().then().catch()}
            />
          )}

          <LanguageSelectorModal
            modalVisible={modalVisible}
            onCloseClick={() => setModalVisible(false)}
          />

          <InviteRelativeModal
            isOpen={inviteOpen}
            onDismiss={() => setInviteOpen(false)}
            onSent={() => {
              setInviteOpen(false);
              alert(t("invite_relative_done"));
            }}
          />

          {getProjectConfig(study_slug).homeExperience === "island-map" && (
            <IonModal
              className="resonance-sheetmodal"
              isOpen={settingsOpen}
              backdropDismiss={true}
              breakpoints={[0, 1]}
              initialBreakpoint={1}
              canDismiss={true}
              onDidDismiss={() => setSettingsOpen(false)}
            >
              <IonToolbar>
                <IonTitle>Island Setting</IonTitle>
                <CloseButton slot="end" onClick={() => setSettingsOpen(false)}>
                  ×
                </CloseButton>
              </IonToolbar>

              <Suspense fallback={null}>
                <IslandSettings />
              </Suspense>
            </IonModal>
          )}

          <ReportBugModal
            isOpen={reportBugOpen}
            onDismiss={() => setReportBugOpen(false)}
            user={user}
            study={study}
          />
          <AboutModal
            isOpen={isAboutOpen}
            onDismiss={() => setIsAboutOpen(false)}
          />
        </div>
      </MenuList>
    </Modal>
  );
};

const Modal = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: ${({ $side }) => ($side === "left" ? "flex-start" : "flex-end")};
  z-index: 99999999;
`;

/* Same 70% slide the drawer always had, mirrored per docking side. */
const slideFromRight = keyframes`
  from {
    margin-right: -70%;
  }
  to {
    margin-right: 0;
  }
`;

const slideFromLeft = keyframes`
  from {
    margin-left: -70%;
  }
  to {
    margin-left: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const MenuList = styled.div<{ $side: "left" | "right" }>`
  width: 70%;
  max-width: 400px;
  animation: ${({ $side }) =>
    css`${$side === "left" ? slideFromLeft : slideFromRight} 0.1s ease`};
  background-color: var(--app-color-background, white);
`;

/* Header band follows the toolbar rule: primary (chrome) with its own
   contrast ink, so the ink can never land unreadably on the band color under
   custom themes. */
const Header = styled.div`
  height: 70px;
  background-color: var(--app-color-primary, #94f1ef);
  color: var(--app-color-primary-contrast, #041723);
  display: flex;
  align-items: center;
`;

/* Monochrome menu glyphs ship as #4285F4-filled SVGs; an <img> can't be
   recolored by CSS, so paint the accent token (interactive link icons —
   tokens.ts roles) through the svg's alpha via mask. Badge-style glyphs
   (Déconnexion) knock their symbol out of the disc rather than drawing it in
   white, so they mask too. Only genuinely multicolor art (country flags) stays
   a plain <img> — a mask would flatten it to one color. */
const MaskIcon = styled.span<{ $src: string }>`
  display: inline-block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background-color: var(--app-color-accent, #4285f4);
  -webkit-mask-image: url("${({ $src }) => $src}");
  mask-image: url("${({ $src }) => $src}");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
`;

export default Menu;